"use client";

import { motion, AnimatePresence } from "framer-motion";
import Link from "next/link";
import { useState, useEffect, useRef } from "react";
import NotificationDropdown from "./NotificationDropdown";
import UserMenu from "./UserMenu";
import ThemeSwitch from "./ThemeSwitch";
import { usePathname } from "next/navigation";
import { Logo } from "@/components/Logo";
import { ChevronDown, ChevronUp, Menu } from "lucide-react";
import { clsx } from "clsx";

export default function AppHeader({ 
  title, 
  onToggleMobileNav,
  mobileNavOpen = false
}: { 
  title?: string;
  onToggleMobileNav?: () => void;
  mobileNavOpen?: boolean;
}) {
  const pathname = usePathname();
  const [collapsed, setCollapsed] = useState(false);
  const lastScrollY = useRef(0);

  const getPageTitle = () => {
    if (title) return title.toUpperCase();
    if (pathname === "/") return "DASHBOARD";
    const segment = pathname.split("/")[1];
    return segment ? segment.toUpperCase().replace("-", " ") : "UNIUI";
  };

  useEffect(() => {
    const onScroll = () => {
      const current = window.scrollY || 0;
      // Desktop: show on scroll down, hide on scroll up
      // Mobile: keep header visible so the hamburger remains available
      if (current > 80 && current > lastScrollY.current) {
        setCollapsed(false);
      } else if (current < lastScrollY.current - 40 || current < 20) {
        setCollapsed(true);
      }
      lastScrollY.current = current;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // On mobile, keep the header visible so the hamburger stays accessible.
  // On desktop, allow the normal scroll-hide behavior.
  const effectiveCollapsed = collapsed;

  return (
    <>
      {/* Mobile header — always visible so hamburger remains accessible */}
      <motion.div
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: "52px" }}
        className="md:hidden"
      >
        <div
          className="h-[52px] flex items-center justify-between px-4 sticky top-0 z-40"
          style={{
            background: "var(--card)",
            borderBottom: "1px solid var(--border)",
          }}
        >
          <div className="flex items-center gap-3">
            <button
              onClick={onToggleMobileNav}
              className="p-2 -ml-2 rounded-lg transition-colors"
              style={{ color: "var(--foreground)" }}
              aria-label="Open navigation"
            >
              <Menu size={20} />
            </button>
            <Link href="/" className="flex items-center gap-2">
              <span
                className="text-2xl font-black tracking-tight leading-none"
                style={{ fontFamily: "var(--font-bebas)", color: "var(--y)" }}
              >
                UNIUI
              </span>
            </Link>
          </div>
          <div className="flex items-center gap-2">
            <ThemeSwitch />
          </div>
        </div>
      </motion.div>

      {/* Desktop header — visible by default, hides on scroll up */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className={clsx(
          "sticky top-0 z-40 transition-all duration-300",
          "hidden md:block",
          collapsed
            ? "h-0 border-0 overflow-hidden"
            : "h-[var(--header-height)]",
        )}
        style={
          !collapsed
            ? {
                background: "var(--card)",
                borderBottom: "1px solid var(--border)",
              }
            : undefined
        }
      >
        <div className="h-[var(--header-height)] px-6 flex items-center justify-between">
          {/* Left: Logo + page title */}
          <div className="flex items-center gap-6">
            <Link
              href="/"
              className="hover:opacity-80 transition-opacity flex items-center gap-2"
            >
              <span
                className="text-3xl font-black tracking-tight leading-none"
                style={{ fontFamily: "var(--font-bebas)", color: "var(--y)" }}
              >
                UNIUI
              </span>
            </Link>
            <div className="h-5 w-px" style={{ background: "var(--border)" }} />
            <span
              className="text-xs font-extrabold uppercase tracking-widest"
              style={{
                color: "var(--muted-foreground)",
                fontFamily: "var(--font-syne)",
              }}
            >
              {getPageTitle()}
            </span>
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-2">
            <ThemeSwitch />
            <NotificationDropdown />
            <div
              className="h-4 w-px mx-1 hidden sm:block"
              style={{ background: "var(--border)" }}
            />
            <UserMenu />
            <button
              onClick={() => setCollapsed(true)}
              className="p-2 rounded-lg transition-colors ml-1"
              style={{ color: "var(--muted-foreground)" }}
              aria-label="Collapse header"
            >
              <ChevronUp size={18} />
            </button>
          </div>
        </div>
      </motion.header>
    </>
  );
}
